import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, FolderOpen, Trash2, Eye, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function CaseHistoryPage() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/cases').then(r => setCases(r.data)),
      api.get('/audit-logs').then(r => setAuditLogs(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const deleteCase = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this case? This cannot be undone.')) return;
    try {
      await api.delete(`/cases/${id}`);
      setCases(cases.filter(c => c.id !== id));
      toast.success('Case deleted');
    } catch {
      toast.error('Failed to delete case');
    }
  };

  const filtered = cases.filter(c =>
    c.patient_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.patient_id?.toLowerCase().includes(search.toLowerCase()) ||
    c.id?.toLowerCase().includes(search.toLowerCase())
  );

  const topFinding = (c) => {
    if (!c.analysis?.diseases?.length) return '—';
    const top = c.analysis.diseases.reduce((a, b) => a.probability > b.probability ? a : b);
    return `${top.name} (${(top.probability * 100).toFixed(0)}%)`;
  };

  return (
    <div className="space-y-6" data-testid="case-history-page">
      <div>
        <h1 className="text-3xl font-heading font-medium tracking-tight text-stone-900">Case History</h1>
        <p className="text-stone-500 mt-1">View and manage all your diagnostic cases</p>
      </div>

      <Tabs defaultValue="cases">
        <TabsList className="bg-stone-100">
          <TabsTrigger value="cases" className="data-[state=active]:bg-white">Cases</TabsTrigger>
          <TabsTrigger value="audit" className="data-[state=active]:bg-white">Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="cases" className="mt-4">
          <Card className="border-stone-200 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-4">
                <CardTitle className="text-lg font-heading">All Cases ({filtered.length})</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input data-testid="case-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or ID..." className="pl-9 h-9" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-12 bg-stone-100 rounded animate-pulse" />)}</div>
              ) : !filtered.length ? (
                <div className="text-center py-12">
                  <FolderOpen className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">{search ? 'No matching cases' : 'No cases yet'}</p>
                </div>
              ) : (
                <Table data-testid="case-history-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase tracking-wider">Date</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Type</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Patient ID</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Patient</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Age/Gender</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Top Finding</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(c => (
                      <TableRow key={c.id} data-testid={`case-row-${c.id}`} className="cursor-pointer hover:bg-stone-50" onClick={() => navigate(`/case/${c.id}`)}>
                        <TableCell className="text-sm text-stone-600">{new Date(c.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          {(() => {
                            const ct = c.case_type === 'bone_fracture' ? 'extremity' : c.case_type;
                            if (ct === 'extremity') return <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">Extremity</Badge>;
                            if (ct === 'abdominal') return <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 bg-blue-50">Abdominal</Badge>;
                            return <Badge variant="outline" className="text-[10px] border-stone-300 text-stone-600">Chest</Badge>;
                          })()}
                        </TableCell>
                        <TableCell className="text-xs font-mono text-[#1A4D2E] font-medium">
                          {c.patient_id ? (
                            <button onClick={(e) => { e.stopPropagation(); navigate(`/patient/${c.patient_id}`); }} className="hover:underline" data-testid={`patient-link-${c.patient_id}`}>
                              {c.patient_id}
                            </button>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="text-sm font-medium text-stone-900">{c.patient_name}</TableCell>
                        <TableCell className="text-sm text-stone-600">{c.patient_age}y, {c.patient_gender}</TableCell>
                        <TableCell className="text-sm text-stone-600">{topFinding(c)}</TableCell>
                        <TableCell>
                          <Badge variant={c.status === 'analyzed' ? 'default' : 'outline'}
                            className={c.status === 'analyzed' ? 'bg-[#1A4D2E] text-white' : ''}>
                            {c.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); navigate(`/case/${c.id}`); }}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={(e) => deleteCase(c.id, e)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <Card className="border-stone-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading">Audit Log</CardTitle>
            </CardHeader>
            <CardContent>
              {!auditLogs.length ? (
                <p className="text-sm text-stone-500 py-8 text-center">No audit entries yet</p>
              ) : (
                <Table data-testid="audit-log-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase tracking-wider">Timestamp</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Action</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Case ID</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLogs.map((log, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-sm text-stone-600">{new Date(log.timestamp).toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs capitalize">{log.action.replace('_', ' ')}</Badge>
                        </TableCell>
                        <TableCell className="text-xs font-mono text-stone-500">{log.case_id ? log.case_id.slice(0, 8) + '...' : '—'}</TableCell>
                        <TableCell className="text-sm text-stone-600">{log.details || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
